import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Spin, Layout, message } from 'antd';
import { useTranslation } from 'react-i18next';
import LoginPage from './components/LoginPage';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import SubtitleEditor from './components/SubtitleEditor';
import HistoryPage from './components/HistoryPage';
import AudioManager from './components/AudioManager';
import SubtitleManager from './components/SubtitleManager';
import RuleManager from './components/RuleManager';
import AdminPage from './components/AdminPage';
import PlanPage from './components/PlanPage';

const { Content } = Layout;

function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 获取当前用户信息
  const fetchCurrentUser = async () => {
    try {
      const response = await fetch('/auth/current_user', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          setUser(null);
          return;
        }
        throw new Error(t('app.fetchUserFail'));
      }

      const data = await response.json();
      if (data.user) {
        setIsAuthenticated(true);
        setUser(data.user);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error(t('app.fetchUserError'), error);
      message.error(t('app.fetchUserFail'));
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载时检查用户状态
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // 定期检查用户会话状态
  useEffect(() => {
    const checkInterval = setInterval(fetchCurrentUser, 5 * 60 * 1000); // 每5分钟检查一次
    return () => clearInterval(checkInterval);
  }, []);

  // 添加全局错误处理
  useEffect(() => {
    const handleError = (error) => {
      console.error('全局错误:', error);
      message.error(t('app.globalError'));
    };

    const handleUnauthorized = async (response) => {
      if (response.status === 401) {
        setIsAuthenticated(false);
        setUser(null);
        // 不要全局重定向，保持在当前页面（例如 /app/login）
        // message.error(t('app.sessionExpired'));
      }
    };

    // 拦截所有 fetch 请求
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        await handleUnauthorized(response);
        return response;
      } catch (error) {
        handleError(error);
        throw error;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);

    return () => {
      window.fetch = originalFetch;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" tip={t('common.loading')} />
      </div>
    );
  }

  return (
    <Router basename="/app">
      <Layout style={{ minHeight: '100vh' }}>
        <Content>
          <Routes>
            <Route 
              path="/" 
              element={<LandingPage />} 
            />
            <Route 
              path="/dashboard" 
              element={isAuthenticated ? <Dashboard user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/editor/:id" 
              element={
                isAuthenticated ? (
                  <SubtitleEditor user={user} />
                ) : (
                  <Navigate to="/" state={{ from: window.location.pathname }} />
                )
              } 
            />
            <Route 
              path="/history" 
              element={isAuthenticated ? <HistoryPage user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/audio-manager" 
              element={isAuthenticated ? <AudioManager user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/subtitle-manager" 
              element={isAuthenticated ? <SubtitleManager user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/rule-manager" 
              element={isAuthenticated ? <RuleManager user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/plan" 
              element={isAuthenticated ? <PlanPage user={user}/> : <Navigate to="/" />} 
            />
            <Route 
              path="/admin" 
              element={isAuthenticated && user.role==='admin' ? <AdminPage user={user}/> : <Navigate to="/" />} 
            />
            <Route 
              path="/login" 
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />} />
            <Route 
              path="/logout" 
              element={
                <Navigate to="/" replace />
              } 
            />
            <Route 
              path="*" 
              element={
                <div style={{ 
                  textAlign: 'center', 
                  padding: '50px' 
                }}>
                  <h2>{t('app.notFoundTitle')}</h2>
                  <button 
                    onClick={() => window.history.back()}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#1890ff',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {t('common.back')}
                  </button>
                </div>
              } 
            />
          </Routes>
        </Content>
      </Layout>
    </Router>
  );
}

export default App;